import { Icons, Typography } from '@jcm/design-system';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, InputBase } from '@mui/material';
import React, { useRef, useState } from 'react';
import PopConfirmButton from '../../../../../../../../shared/components/popConfirmButton/popConfirmButton';
import TruncatedTextTooltip from '../../../../../../../../shared/components/truncatedTextTooltip/TruncatedTextTooltip';
import ObrigacaoService from '../../../../../services/Obrigacoes/Obrigacoes.service';
import { Anexo } from '../../../../../ts/types/ObrigacaoEtapa/common/Anexos';
import AvatarGroupComponent from '../../AvatarGroupComponent/AvatarGroupComponent';
import styles from './index.module.scss';

interface ListaAnexosProps {
	anexos: Anexo[];
	onDelete: (index: number) => void;
	onEdit: (index: number, descricao: string) => void;
	onSuccess: (message: string) => void;
	onError: (message: string) => void;
	disabled: boolean;
	usuario: any;
}

const ListaAnexos: React.FC<ListaAnexosProps> = ({
	anexos,
	onDelete,
	onEdit,
	onSuccess,
	onError,
	disabled,
	usuario,
}) => {
	const [editIndex, setEditIndex] = useState<number | null>(null);
	const [newDescricao, setNewDescricao] = useState<string>('');
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const obrigacaoService = new ObrigacaoService();

	const handleGetUserEmpresaId = () => {
		return usuario?.participacaoEmpresa?.[0]?.empresaId || usuario?.empresaSalvaId;
	};

	const handleEditClick = (index: number, descricao: string) => {
		if (!disabled) {
			setEditIndex(index);
			setNewDescricao(descricao);
		}
	};

	const handleSaveEdit = async () => {
		if (editIndex !== null && newDescricao.trim()) {
			try {
				await onEdit(editIndex, newDescricao);
				setEditIndex(null);
				setNewDescricao('');
				onSuccess('Edição salva com sucesso!');
			} catch {
				onError('Erro ao salvar edição. Tente novamente.');
			}
		}
	};

	const handleDelete = async (index: number) => {
		try {
			await onDelete(index);
			onSuccess('Anexo excluído com sucesso!');
		} catch {
			onError('Erro ao excluir o anexo. Tente novamente.');
		}
	};

	const handleCancelEdit = () => {
		setEditIndex(null);
		setNewDescricao('');
	};

	const renderAvatars = (nome: string) => {
		return [{ id: 1, name: nome.charAt(0), nomeCompleto: nome }];
	};

	const handleDownload = async (anexoId: number, empresaSalvaId: number | undefined, nomeOriginal: string) => {
		try {
			if (!empresaSalvaId) {
				onError('ID da empresa não encontrado.');
				return;
			}

			await obrigacaoService.downloadAnexoObrigacao(empresaSalvaId, anexoId, nomeOriginal);
			onSuccess('Download iniciado com sucesso!');
		} catch (error) {
			console.error('Erro ao baixar o anexo:', error);
			onError('Erro ao baixar o anexo. Tente novamente.');
		}
	};

	return (
		<Box className={styles.listaAnexosContainer}>
			<Grid container spacing={2} className={styles.headerRow}>
				<Grid item xs={4}>
					<Typography.Body variant='default'>Arquivo</Typography.Body>
				</Grid>
				<Grid item xs={3}>
					<Typography.Body variant='default'>Descrição</Typography.Body>
				</Grid>
				<Grid item xs={2}>
					<Typography.Body variant='default'>Data</Typography.Body>
				</Grid>
				<Grid item xs={2}>
					<Typography.Body variant='default'>Autor</Typography.Body>
				</Grid>
				<Grid item xs={1}>
					<Typography.Body variant='default'>Excluir</Typography.Body>
				</Grid>
			</Grid>

			{anexos.length === 0 ? (
				<Typography.Body variant='default' className={styles.noAnexosText}>
					Nenhum arquivo anexado
				</Typography.Body>
			) : (
				anexos.map((anexo, index) => (
					<Grid container spacing={1} key={index} className={styles.anexoRow}>
						<Grid item xs={4}>
							<Typography.Body
								variant='default'
								onClick={() => handleDownload(anexo.id, handleGetUserEmpresaId(), anexo.nomeOriginal)}
								className={styles.typographyClickable}
								style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
							>
								<TruncatedTextTooltip text={anexo.nomeOriginal} maxWidth='200px' />
							</Typography.Body>
						</Grid>

						<Grid item xs={3} className={styles.descricaoCell}>
							{editIndex === index ? (
								<div className={styles.descricaoWrapper} ref={wrapperRef}>
									<InputBase
										value={newDescricao}
										onChange={(e) => setNewDescricao(e.target.value)}
										autoFocus
										multiline
										className={styles.descricaoInput}
										fullWidth
										disabled={disabled}
									/>
									<IconButton
										onClick={handleSaveEdit}
										size='small'
										className={styles.descricaoBotaoCheck}
										disabled={disabled}
									>
										<CheckIcon fontSize='small' />
									</IconButton>
									<IconButton
										onClick={handleCancelEdit}
										size='small'
										className={styles.descricaoBotaoNoCheck}
										disabled={disabled}
									>
										<CloseIcon fontSize='small' />
									</IconButton>
								</div>
							) : (
								<div className={styles.descricaoTextoWrapper}>
									<Typography.Body
										variant='default'
										onClick={() => handleEditClick(index, anexo.descricao)}
										className={styles.descricaoTexto}
										style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
									>
										{anexo.descricao}
									</Typography.Body>
									{!disabled && <Icons.Edit className={styles.hoverIcon} style={{ fontSize: '16px' }} />}
								</div>
							)}
						</Grid>
						<Grid item xs={2}>
							<Typography.Body variant='default'>{anexo.dataCriacao}</Typography.Body>
						</Grid>
						<Grid item xs={2}>
							<AvatarGroupComponent
								members={renderAvatars(anexo.funcionario?.nome || '')}
								color='#2F2E2E'
								bgColor='#80D4D5'
							/>
						</Grid>
						<Grid item xs={1}>
							<PopConfirmButton
								onConfirm={() => handleDelete(index)}
								title='Você tem certeza que deseja excluir?'
								okText='Sim, excluir!'
								cancelText='Cancelar'
								buttonText={<Icons.Delete style={{ fontSize: '22px' }} />}
								disabled={disabled}
							/>
						</Grid>
					</Grid>
				))
			)}
		</Box>
	);
};

export default ListaAnexos;
